/**
 * Per-company settings: VAT registration and period, accounting method, batch
 * threshold, default payment source and onboarding progress. One row per
 * company, id == companyId. Pure defaults live in `./defaults`.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { CompanySettings, Uuid } from '../types';
import { defaultCompanySettings } from './defaults';

export { ALWAYS_NEEDS_YOU_ORE, DEFAULT_BATCH_THRESHOLD_ORE, defaultCompanySettings, defaultVatRegistered, type CompanySettingsOverrides } from './defaults';

export type CompanySettingsPatch = Partial<Omit<CompanySettings, 'id' | 'companyId' | 'updatedAt' | 'onboarding'>> & {
  onboarding?: Partial<CompanySettings['onboarding']>;
};

/**
 * Settings for a company. A missing row (should not happen after the v2
 * upgrade) is answered with the entity default without writing anything, so
 * read paths such as live queries stay side-effect free.
 */
export async function getCompanySettings(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<CompanySettings> {
  const stored = await db.companySettings.get(companyId);
  if (stored) return stored;
  const company = await db.companies.get(companyId);
  if (!company) throw new Error('Company not found');
  return defaultCompanySettings(company);
}

export async function updateCompanySettings(companyId: Uuid, patch: CompanySettingsPatch, db: BookkeepingDB = defaultDb): Promise<CompanySettings> {
  return db.transaction('rw', db.companySettings, db.companies, async () => {
    const current = await getCompanySettings(companyId, db);
    const { onboarding, ...rest } = patch;
    const next: CompanySettings = { ...current, updatedAt: new Date().toISOString() };
    for (const [k, v] of Object.entries(rest)) {
      if (v !== undefined) (next as unknown as Record<string, unknown>)[k] = v;
    }
    if (onboarding) {
      next.onboarding = { ...current.onboarding };
      for (const [k, v] of Object.entries(onboarding)) {
        if (v !== undefined) (next.onboarding as unknown as Record<string, unknown>)[k] = v;
      }
    }
    if (!Number.isInteger(next.batchThresholdOre) || next.batchThresholdOre < 0) {
      throw new Error('batchThresholdOre must be a non-negative integer in öre');
    }
    await db.companySettings.put(next);
    return next;
  });
}

export async function markOnboardingCompleted(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
  await updateCompanySettings(companyId, { onboarding: { completedAt: new Date().toISOString() } }, db);
}
